import React, { useState } from 'react';

import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { basisPointsToPercent } from 'features/deals/DealStages/EventActionCards/utils';
import { Accordion, Checkbox } from 'ui-kit';
import { formatRelativeDateTime } from 'utils/dateUtils';

import { useActiveTradesContext } from './ActiveTradesContext';
import BidDetails from './BidDetails';
import { ListingWithBids } from './BidsTab';

type Props = {
  listing: ListingWithBids;
};

const BidAccordionCards = ({ listing }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { selectedListingIds, addSelectedListingId, removeSelectedListingId } =
    useActiveTradesContext();

  if (!listing.deals?.length) return null;

  const onCheckboxChange = (e: React.MouseEvent<HTMLDivElement>, dealBidId: string) => {
    e.stopPropagation();
    selectedListingIds.includes(dealBidId)
      ? removeSelectedListingId(dealBidId)
      : addSelectedListingId(dealBidId);
  };

  return (
    <>
      {listing.deals.map((deal, idx) => {
        const latestBid = deal.bids[deal.bids.length - 1];
        if (!latestBid) return null;

        const bidsHistory = deal.bids.slice(0, -1);
        const dealId = deal.id;
        const dealBidId = `${dealId}:${latestBid.bid_id}`;
        const bidNumber = `BID ${idx + 1}`;

        const checked = selectedListingIds.includes(dealBidId);
        const carveName = latestBid.carve?.length
          ? latestBid.carve[0].operand || 'Unnamed Carve'
          : 'Original Bid';

        const bidBasisPoints = latestBid?.stipulations?.bid_basis_points ?? 0;
        const pricePercent = basisPointsToPercent(bidBasisPoints);

        const display = formatRelativeDateTime(latestBid.created_date);

        return (
          <Accordion
            key={dealBidId}
            label={
              <div
                className="w-full cursor-pointer pl-4"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <div className="flex w-full items-center gap-4 border-l-4 border-yellow-400 bg-background-canvas py-2 pl-5 hover:bg-brand-950">
                  <div className="flex items-center text-white">
                    {isOpen ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={checked}
                      onChange={(e) => onCheckboxChange(e, dealBidId)}
                      disabled={false}
                    />
                    <span className="text-lg text-white">{bidNumber}:</span>
                  </div>
                  <span className="text-gray-400">{carveName}</span>
                  <div>
                    <span className="ml-auto mr-2 text-white">Price:</span>
                    <span className="text-gray-400">{pricePercent}%</span>
                  </div>
                  <div className="ml-[520px]">
                    <span className="text-gray-400">{display}</span>
                  </div>
                </div>
              </div>
            }
            initialIsOpen={false}
            hasBorder={false}
            showIcon={false}
          >
            <BidDetails
              bid={latestBid}
              bidsHistory={bidsHistory}
              performanceSummary={deal.performance_summary}
              dealId={dealId}
            />
          </Accordion>
        );
      })}
    </>
  );
};

export default BidAccordionCards;
